import { useEffect, useState } from "react";

import { MetadataFilters } from "@/components/chat/types";
import {
  EnumeratedIconikAsset,
  GetAssetsResponse,
  IconikAsset,
} from "@/components/iconik/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

interface UseAssetsProps {
  gameId?: string;
  playId?: string;
  assetId?: string;
  /**
   * Response-level metadata filters (e.g. ``{ FeedType: "Films" }``). When
   * present and non-empty, they are sent to ``/assets`` so Iconik returns
   * only matching clips, and they participate in the cache key so a filtered
   * result never collides with the unfiltered one for the same play. Absent
   * or empty leaves the request identical to the pre-filter behaviour.
   */
  filters?: MetadataFilters;
}

interface CachedAssets {
  title: string;
  description?: string;
  items: EnumeratedIconikAsset[];
}

/**
 * Module-level cache of successfully fetched assets, keyed by game/play.
 *
 * Iconik asset cards are rendered inside chat messages. When a subsequent
 * message is sent, the message list re-renders and previously mounted cards
 * can remount, which would otherwise re-trigger the fetch and flash a loading
 * state for content the user has already seen. Caching resolved results lets a
 * remounted card render its data synchronously — no loading flash, no
 * redundant network call. Only successful responses are cached so failures can
 * be retried.
 */
const assetsCache = new Map<string, CachedAssets>();

/**
 * Clear the module-level assets cache.
 *
 * The cached `/assets` responses embed the admin-driven Clip Preview
 * `previewFields`. When an admin changes which fields are shown
 * (showInPreview), those cached responses are stale — revisiting a play would
 * otherwise render the old field set until a hard reload. The admin metadata
 * hook calls this on a successful toggle so the next asset fetch is fresh.
 */
export const clearAssetsCache = (): void => {
  assetsCache.clear();
  // Also forget in-flight requests. They were issued under the *previous*
  // preview-field configuration, so a caller arriving after the clear must
  // start a fresh request rather than join one whose result is already stale.
  inflightRequests.clear();
  // Clearing the two maps is not sufficient on its own: a request issued before
  // this call is still running, and on resolution it would write its pre-toggle
  // payload straight back into the map we just emptied. Bumping the generation
  // is what tells those in-flight requests their result is no longer cacheable.
  cacheGeneration += 1;
};

/**
 * Monotonic counter identifying the current cache epoch.
 *
 * A request captures this when it is issued and re-checks it before writing its
 * result. If an admin toggled `showInPreview` in the meantime the value has
 * moved on, and the request skips the write rather than repopulating a cache
 * that was deliberately emptied.
 *
 * The request still *returns* its payload to the caller that asked for it —
 * that caller's read predates the toggle, and failing it would be worse than
 * serving it slightly stale. What must not happen is one caller's outdated
 * response becoming every later caller's cached answer.
 */
let cacheGeneration = 0;

/**
 * Requests that have been issued but not yet settled, keyed exactly as
 * {@link assetsCache}.
 *
 * `assetsCache` is only written once a response *resolves*, so without this
 * two callers asking for the same key in the same tick both hit the network.
 * That is not hypothetical: the same game/play can appear in more than one
 * answer, React StrictMode double-invokes effects in development, the grouping
 * coordinator races each accordion's own `useAssets` fetch, and two multi-sport
 * cards can name the same asset id. Each duplicate is a full `/assets` round
 * trip (p50 ~2.2s), so collapsing them is worth the bookkeeping.
 *
 * The dedup only covers a caller that consults this map, so every fetch goes
 * through {@link loadAssets} — including the hook's own, which previously
 * reached for `apiGet` directly and therefore could not be collapsed against
 * anything.
 *
 * Entries are removed once settled — including on failure, so a failed fetch
 * is retried rather than replayed to every later caller. That matters because
 * a failure surfaces in the UI as "No Media Found", which would otherwise
 * mislabel a play as media-less for the rest of the session.
 */
const inflightRequests = new Map<string, Promise<CachedAssets>>();

/** Cache key for a single asset addressed by its Iconik id. */
const assetCacheKey = (assetId: string): string => `asset|${assetId}`;

/** True when a filters map has at least one entry. */
const hasFilters = (filters?: MetadataFilters): boolean =>
  !!filters && Object.keys(filters).length > 0;

/**
 * Canonical (key-sorted) serialization of the filters, or "" when there are
 * none. Used for the cache key so ``{a,b}`` and ``{b,a}`` map to one entry.
 *
 * Exported so callers that memoize on the filters can key off the same
 * canonical form the cache does, rather than the object identity — the filters
 * are re-parsed from the answer's markdown on every render, so the object is
 * a new one each time even when its contents have not changed.
 */
export const canonicalFilters = (filters?: MetadataFilters): string => {
  if (!hasFilters(filters)) return "";
  const sorted = Object.keys(filters as MetadataFilters).sort();
  return JSON.stringify(sorted.map((k) => [k, (filters as MetadataFilters)[k]]));
};

const buildCacheKey = (
  gameId: string,
  playId: string,
  filters?: MetadataFilters
): string => {
  const canonical = canonicalFilters(filters);
  // Unfiltered keys are byte-identical to the pre-filter scheme so existing
  // cache entries and callers are unaffected.
  return canonical ? `${gameId}|${playId}|${canonical}` : `${gameId}|${playId}`;
};

/** Build the ``/assets`` request path, appending filters only when present. */
const buildAssetsPath = (
  gameId: string,
  playId: string,
  filters?: MetadataFilters
): string => {
  const base = `/assets?gameId=${gameId}&playId=${playId}`;
  return hasFilters(filters)
    ? `${base}&filters=${encodeURIComponent(JSON.stringify(filters))}`
    : base;
};

/** Coerce an Iconik description (string or array of paragraphs) to a string. */
const normalizeDescription = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map((part) => (typeof part === "string" ? part.trim() : ""))
      .filter(Boolean)
      .join("\n\n");
  }
  return typeof value === "string" ? value : "";
};

const enumerate = (
  assets: IconikAsset[],
  gameId: string,
  playId: string
): EnumeratedIconikAsset[] =>
  assets.map((asset, index) => ({
    ...asset,
    // The /assets API can return description as a list of paragraphs
    // (Iconik metadata values are arrays). Normalize to a single string so
    // the Clip Preview renders cleanly.
    description: normalizeDescription(asset.description),
    // Tag with the play so a cart snapshot can later re-fetch this play and
    // refresh admin-driven fields (e.g. previewFields).
    gameId,
    playId,
    index: index + 1,
  }));

/**
 * Fetch (or read from cache) one play's assets, normalized + enumerated.
 *
 * Shares the module-level `assetsCache` with `useAssets`, so a play loaded
 * here renders instantly when its `IconikAssetsTable` mounts — no double
 * fetch. A play with no media resolves to ``items: []``; a genuine fetch
 * failure throws, so callers can tell "no media" apart from "request failed".
 */
/**
 * The single path from a cache key to its assets: serve from cache, else join a
 * request already in flight for that key, else issue one.
 *
 * Every caller goes through here — the grouping coordinator via
 * {@link loadPlayAssets} and each accordion via {@link loadAssetById} or
 * {@link loadPlayAssets}. That is deliberate on two counts. It is what makes the
 * in-flight dedup actually cover a card mounting while the coordinator's fetch
 * for the same play is outstanding, and it keeps the cache write in exactly one
 * place: the generation check that stops a superseded response repopulating a
 * cleared cache is an invariant that has to hold for *every* writer, and it
 * previously had to be repeated for each one.
 */
const loadAssets = async (
  apiGet: <T>(path: string) => Promise<T | null>,
  {
    key,
    path,
    gameId,
    playId,
    label,
  }: { key: string; path: string; gameId: string; playId: string; label: string }
): Promise<CachedAssets> => {
  const cached = assetsCache.get(key);
  if (cached) return cached;

  // Join a request already in flight for this exact key rather than issuing a
  // second one. See {@link inflightRequests}.
  const pending = inflightRequests.get(key);
  if (pending) return pending;

  // Captured before the request is issued, compared after it resolves.
  const generation = cacheGeneration;

  const request = (async () => {
    const response = await apiGet<GetAssetsResponse>(path);
    if (!response?.assets) {
      throw new Error(`Failed to load assets for ${label}`);
    }
    const result: CachedAssets = {
      title: response.title,
      description: response.description,
      items: enumerate(response.assets, gameId, playId),
    };
    if (generation === cacheGeneration) {
      assetsCache.set(key, result);
    }
    // else: an admin toggled showInPreview while this was in flight, so this
    // payload carries the superseded preview fields. It is returned to the
    // caller that asked for it but never becomes anybody else's cached answer.
    return result;
  })();

  inflightRequests.set(key, request);
  try {
    return await request;
  } finally {
    // Only drop our OWN entry. A clearAssetsCache() plus a fresh request for the
    // same key may have replaced it while this one was awaiting, and deleting by
    // key alone would evict that newer request — after which nothing dedups onto
    // it and every later caller issues its own /assets call.
    //
    // Dropped on success and on failure alike: a resolved value now lives in
    // assetsCache, and a failure must be retryable.
    if (inflightRequests.get(key) === request) {
      inflightRequests.delete(key);
    }
  }
};

export const loadPlayAssets = (
  apiGet: <T>(path: string) => Promise<T | null>,
  gameId: string,
  playId: string,
  filters?: MetadataFilters
): Promise<CachedAssets> =>
  loadAssets(apiGet, {
    key: buildCacheKey(gameId, playId, filters),
    path: buildAssetsPath(gameId, playId, filters),
    gameId,
    playId,
    label: `${gameId}/${playId}`,
  });

/**
 * Fetch (or join/serve from cache) a single asset addressed by its Iconik id.
 *
 * The multi-sport accordions have no game/play structure, so they are the only
 * readers of these keys — and before this shared loader they bypassed the
 * in-flight map entirely, meaning two cards for the same asset always issued two
 * requests.
 */
export const loadAssetById = (
  apiGet: <T>(path: string) => Promise<T | null>,
  assetId: string
): Promise<CachedAssets> =>
  loadAssets(apiGet, {
    key: assetCacheKey(assetId),
    path: `/assets?assetId=${assetId}`,
    // No game/play to tag these assets with; `enumerate` records empty strings,
    // as this path always has.
    gameId: "",
    playId: "",
    label: `asset ${assetId}`,
  });

const useAssets = ({ gameId = "", playId = "", filters, assetId }: UseAssetsProps) => {
  // Stable string form of the filters for the effect dependency + cache key
  // (the object identity is not stable across renders).
  const filtersKey = canonicalFilters(filters);
  const cacheKey = assetId ? `asset|${assetId}` : buildCacheKey(gameId, playId, filters);
  const cached = assetsCache.get(cacheKey);

  const [title, setTitle] = useState<string>(
    cached?.title ?? (assetId ? `Asset ${assetId.slice(0, 8)}...` : `Game ID ${gameId} | Play ID ${playId}`)
  );
  const [description, setDescription] = useState<string | undefined>(
    cached?.description
  );
  const [items, setItems] = useState<EnumeratedIconikAsset[]>(
    cached?.items ?? []
  );
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotifications();

  const { apiGet } = useApi();

  useEffect(() => {
    let cancelled = false;

    // Serve previously fetched assets directly from cache — skip the network
    // request and the loading state entirely.
    const cachedAssets = assetsCache.get(cacheKey);
    if (cachedAssets) {
      setTitle(cachedAssets.title);
      setDescription(cachedAssets.description);
      setItems(cachedAssets.items);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchAssets = async () => {
      // Need either assetId OR (gameId + playId)
      if (!assetId && (!gameId || !playId)) return;

      setLoading(true);

      try {
        // Routed through the shared loader rather than calling apiGet directly,
        // so this card joins a request already in flight for the same key —
        // typically the grouping coordinator's fetch for this very play — and so
        // the cache write and its generation check live in one place instead of
        // being duplicated here.
        const result = assetId
          ? await loadAssetById(apiGet, assetId)
          : await loadPlayAssets(apiGet, gameId, playId, filters);

        // Bail out if this fetch is no longer relevant (unmounted or the
        // game/play changed while the request was in flight).
        if (cancelled) return;

        setTitle(result.title);
        setDescription(result.description);
        setItems(result.items);
      } catch (error) {
        // The shared loader throws where this used to handle a malformed
        // response inline, so both failures — a rejected request and a 200
        // carrying no `assets` array — arrive here and reach the same
        // notification rather than silently rendering an empty card.
        if (cancelled) return;
        console.error(error);
        notifyError("Iconik Assets Error", "Failed to load Iconik assets");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAssets();

    return () => {
      cancelled = true;
    };
    // filtersKey captures a filter change; gameId/playId capture the play.
  }, [gameId, playId, filtersKey, assetId]);

  return {
    title,
    description,
    items,
    loading,
  };
};

export default useAssets;
